import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FinecoProductCardType,
  ProductCardProductsType,
  ProductCardCompareLabelWeightsType,
  ProductCardIsinToNameType,
  ProductCardDateValuesType,
  ProductCardMisureDiRischioType,
} from "types/AssistantTypes";
import styles from "./finecoProductCardCompare.module.scss";
import InformazioniGeneraliCompare from "./InformazioniGeneraliCompare";
import AnagraficaCompare from "./AnagraficaCompare";
import RendimentiPerAnnoSolareCompare from "./RendimentiPerAnnoSolareCompare";
import MisureDiRischioCompare from "./MisureDiRischioCompare";
import RendimentiCumulatiCompare from "./RendimentiCumulatiCompare";
import GenericBarChartCompare from "./GenericBarChartCompare";
import ProductCommonTableCompare from "./ProductCommonTableCompare";
import RendimentiAndamentoCompareContainer from "./RendimentiAndamentoCompareContainer";
import { FinecoButton } from "pages/assistant/components/FinecoButton";
import {
  areAllProductsBonds,
  mapMisureDiRischioData,
  mapSettorialeData,
} from "./utils";

import { printHtmlElement } from "utils/printUtils";

type FinecoProductCardProps = {
  data: FinecoProductCardType & {
    product_types?: Record<string, string>;
  };
};

const FinecoProductCardCompare: React.FC<FinecoProductCardProps> = memo(
  ({ data }) => {
    const { t } = useTranslation();

    const cardRef = useRef<HTMLDivElement>(null);
    const [isPrinting, setIsPrinting] = useState(false);

    // Function to get PDF via browser print dialog
    const handlePrint = () => {
      if (cardRef.current) {
        printHtmlElement({
          element: cardRef.current,
          title: "Product Analysis Compare",
          loadingMessage: t("productCard.preparing") || "Preparing document...",
        });
      }
    };

    useEffect(() => {}, []);

    const {
      isAllBonds,
      allProducts,
      productTypes,
      isinToName,
      performanceProducts,
      informazioniPrincipali,
      anagrafica,
      andamentoData,
      drawdownData,
      rendimentiPerAnnoSolareData,
      misureDiRischioData,
      rendimentiCumulatiData,
      distribuzioneAssetData,
      esposizioneValutariaData,
      esposizioneGeograficaData,
      esposizioneSettorialeParteAzionariaData,
      esposizioneSettorialeParteObbligazionariaData,
      isin,
    } = useMemo(() => {
      const allProducts = data?.all_products as ProductCardProductsType;
      // Provide default empty arrays for all data to prevent undefined access
      const informazioniPrincipali = (data?.informazioni_principali ||
        []) as ProductCardCompareLabelWeightsType[];
      const anagrafica = (data?.anagrafica ||
        []) as ProductCardCompareLabelWeightsType[];
      const productTypes = data?.product_types || {};
      const isAllBonds = areAllProductsBonds(productTypes);

      // Transform data for bonds
      const transformedMisureDiRischio = mapMisureDiRischioData(
        (data?.misure_di_rischio || []) as ProductCardMisureDiRischioType[],
        productTypes,
        allProducts,
      );

      const transformedEsposizioneSettorialeParteAzionaria = mapSettorialeData(
        (data?.esposizione_settoriale_parte_azionaria ||
          []) as ProductCardCompareLabelWeightsType[],
        productTypes,
        allProducts,
      );

      const transformedEsposizioneSettorialeParteObbligazionaria =
        mapSettorialeData(
          (data?.esposizione_settoriale_parte_obbligazionaria ||
            []) as ProductCardCompareLabelWeightsType[],
          productTypes,
          allProducts,
        );

      return {
        isAllBonds,
        allProducts,
        productTypes,
        isinToName: data?.isin_to_name as ProductCardIsinToNameType,
        performanceProducts:
          data?.performance_products as ProductCardProductsType,
        informazioniPrincipali,
        anagrafica,
        andamentoData: (data?.andamento || []) as ProductCardDateValuesType[],
        drawdownData: (data?.drawdown || []) as ProductCardDateValuesType[],
        rendimentiPerAnnoSolareData: (data?.rendimenti_per_anno_solare ||
          []) as ProductCardCompareLabelWeightsType[],
        misureDiRischioData: transformedMisureDiRischio,
        rendimentiCumulatiData: (data?.rendimenti_cumulati ||
          []) as ProductCardMisureDiRischioType[],
        distribuzioneAssetData: (data?.distribuzione_asset ||
          []) as ProductCardCompareLabelWeightsType[],
        esposizioneValutariaData: (data?.esposizione_valutaria ||
          []) as ProductCardCompareLabelWeightsType[],
        esposizioneGeograficaData: (data?.esposizione_geografica ||
          []) as ProductCardCompareLabelWeightsType[],
        esposizioneSettorialeParteAzionariaData: isAllBonds
          ? []
          : transformedEsposizioneSettorialeParteAzionaria,
        esposizioneSettorialeParteObbligazionariaData: isAllBonds
          ? []
          : transformedEsposizioneSettorialeParteObbligazionaria,
        title:
          informazioniPrincipali.find((item) => item.label === "Nome")
            ?.weights || "",
        date:
          informazioniPrincipali.find((item) => item.label === "Date")
            ?.weights || "",
        isin:
          informazioniPrincipali.find((item) => item.label === "ISIN")
            ?.weights || "",
      };
    }, [data]);

    return (
      <div className={styles.cardContainer} ref={cardRef}>
        <InformazioniGeneraliCompare
          data={informazioniPrincipali}
          allProducts={allProducts}
          isinToName={isinToName}
          productTypes={productTypes}
        />
        <div className="force-page-break"></div>
        <AnagraficaCompare
          data={anagrafica}
          allProducts={allProducts}
          isinToName={isinToName}
          productTypes={productTypes}
        />
        <div className="force-page-break"></div>
        <RendimentiAndamentoCompareContainer
          andamento={andamentoData}
          drawdown={drawdownData}
          performanceProducts={performanceProducts}
          isinToName={isinToName}
        />
        <div className={styles.cardPadding}>
          <RendimentiPerAnnoSolareCompare
            data={rendimentiPerAnnoSolareData}
            isinToName={isinToName}
            performanceProducts={performanceProducts}
          />
        </div>
        {/* Rendimento per anno solare Chart */}
        <ProductCommonTableCompare
          data={rendimentiPerAnnoSolareData}
          allProducts={allProducts}
          isinToName={isinToName}
          subtitle={t("productCard.mainInfo")}
          withPesoRow={false}
          addPercentage={true}
          marginTop={24}
        />
        <div className="force-page-break"></div>
        <MisureDiRischioCompare
          data={misureDiRischioData}
          allProducts={allProducts}
          isinToName={isinToName}
          isAllBondProducts={isAllBonds}
        />
        <div className="force-page-break"></div>
        <RendimentiCumulatiCompare
          data={rendimentiCumulatiData}
          allProducts={allProducts}
          isinToName={isinToName}
          isAllBondProducts={isAllBonds}
        />
        <div className={styles.cardPadding}>
          <GenericBarChartCompare
            data={distribuzioneAssetData}
            allProducts={allProducts}
            isinToName={isinToName}
            title={t("productCard.distribuzioneAsset")}
          />
        </div>
        {/* Esposizione Valutaria */}
        <div className="force-page-break"></div>
        <ProductCommonTableCompare
          data={esposizioneValutariaData}
          allProducts={allProducts}
          isinToName={isinToName}
          subtitle={t("productCard.distribuzionePerValuta")}
          title={t("productCard.esposizioneValutaria")}
          withPesoRow={true}
        />
        {/* Esposizione Geografica */}
        <div className="force-page-break"></div>
        <ProductCommonTableCompare
          data={esposizioneGeograficaData}
          allProducts={allProducts}
          isinToName={isinToName}
          subtitle={t("productCard.areeGeografiche")}
          title={t("productCard.esposizioneGeografica")}
          withPesoRow={true}
        />
        {/* Esposizione settoriale - Parte Azionaria (%) */}
        {esposizioneSettorialeParteAzionariaData.length > 0 && (
          <>
            <div className="force-page-break"></div>
            <ProductCommonTableCompare
              data={esposizioneSettorialeParteAzionariaData}
              allProducts={allProducts}
              isinToName={isinToName}
              subtitle={t("productCard.distribuzionePerSettore")}
              title={t("productCard.esposizioneSettorialeAzionaria")}
              withPesoRow={true}
            />
          </>
        )}
        {/* Esposizione settoriale - Parte Obbligazionaria (%) */}
        {esposizioneSettorialeParteObbligazionariaData.length > 0 && (
          <>
            <div className="force-page-break"></div>
            <ProductCommonTableCompare
              data={esposizioneSettorialeParteObbligazionariaData}
              allProducts={allProducts}
              isinToName={isinToName}
              subtitle={t("productCard.tipoDiBond")}
              title={t("productCard.esposizioneSettorialeObbligazionaria")}
              withPesoRow={true}
            />
          </>
        )}

        {/* <div
          className="print-button-container"
          style={{
            marginTop: "30px",
            display: "flex",
            gap: "10px",
            flexWrap: "wrap",
            marginLeft: "auto",
            justifyContent: "flex-end",
          }}
        >
          <div>
            <FinecoButton
              click={handlePrint}
              disabled={isPrinting}
              copy={
                isPrinting
                  ? t("productCard.downloadingProductCard").toUpperCase()
                  : t("productCard.downloadProductCardCompare").toUpperCase()
              }
            />
          </div>
        </div> */}
      </div>
    );
  },
);

FinecoProductCardCompare.displayName = "FinecoProductCardCompare";

export default FinecoProductCardCompare;
